// Qué decir cuando `GET /portal/catalog` falla, por `code`, y cómo se resuelve un `?sku=` que todavía
// no llegó entre las páginas cargadas. Puro.
import { ApiError, INTERNAL_ERROR_MESSAGE } from '@/api/errors';

export interface CatalogListErrorCopy {
    title: string;
    description: string;
    /** Falso: insistir no cambia el resultado, no se pinta «Reintentar». */
    canRetry: boolean;
}

const CATALOG_UNAVAILABLE = 'CATALOG_UNAVAILABLE';
const INVALID_NEXT_TOKEN = 'INVALID_NEXT_TOKEN';
const PERMISSION_DENIED = 'PERMISSION_DENIED';

function codeOf(error: unknown): string | null {
    return error instanceof ApiError ? error.code : null;
}

/** La primera página no llegó: no hay tabla que pintar, solo el motivo. */
export function catalogListErrorCopy(error: unknown): CatalogListErrorCopy {
    const code = codeOf(error);
    if (code === PERMISSION_DENIED) {
        return {
            title: 'No tienes permiso para ver el catálogo de tu tienda.',
            description:
                'Tu rol no incluye el catálogo del portal; pídele a tu gerente que te lo habilite.',
            canRetry: false,
        };
    }
    if (code === CATALOG_UNAVAILABLE) {
        return {
            title: 'El inventario de tu tienda no respondió.',
            description: 'Vuelve a intentarlo en un momento; si sigue igual, avísale a tu gerente.',
            canRetry: true,
        };
    }
    return {
        title: 'No se pudo leer el catálogo de la tienda.',
        description: 'Vuelve a intentarlo; si sigue igual, avísale a tu gerente.',
        canRetry: true,
    };
}

/** El cursor venció: la siguiente página no sale con él y la lista se vuelve a pedir desde cero. */
export function mustRestartCatalog(error: unknown): boolean {
    return codeOf(error) === INVALID_NEXT_TOKEN;
}

/** Por qué no llegó la página siguiente; lo ya cargado se queda. */
export function catalogNextPageReason(error: unknown): string {
    if (mustRestartCatalog(error)) {
        return 'la lista cambió mientras la recorrías; «Reintentar» la vuelve a cargar desde el inicio';
    }
    if (codeOf(error) === CATALOG_UNAVAILABLE) return 'el inventario de tu tienda no respondió';
    if (codeOf(error) === PERMISSION_DENIED) return 'tu rol no incluye el catálogo del portal';
    return INTERNAL_ERROR_MESSAGE;
}

/** Páginas que se recorren solas buscando el `?sku=` antes de pedirle al vendedor que lo busque. */
export const DEEP_LINK_MAX_PAGES = 8;

/** Páginas que se traen solas al abrir: agrupar por modelo pide todas las variantes cargadas. */
export const AUTOLOAD_MAX_PAGES = DEEP_LINK_MAX_PAGES;

/** ¿Toca traer otra página sola? Se detiene al fallar una, al tope o cuando ya no hay más. */
export function shouldAutoLoad(input: {
    hasNextPage: boolean;
    isFetching: boolean;
    nextPageFailed: boolean;
    pagesLoaded: number;
}): boolean {
    return (
        input.hasNextPage &&
        !input.isFetching &&
        !input.nextPageFailed &&
        input.pagesLoaded > 0 &&
        input.pagesLoaded < AUTOLOAD_MAX_PAGES
    );
}

export type DeepLinkState = 'none' | 'found' | 'searching' | 'beyondLoaded' | 'notFound';

/** En qué va la búsqueda del SKU del enlace entre lo cargado. */
export function deepLinkStateOf(input: {
    sku: string | undefined;
    loadedSkus: readonly string[];
    isPending: boolean;
    hasNextPage: boolean;
    pagesLoaded: number;
    nextPageFailed: boolean;
}): DeepLinkState {
    if (input.sku === undefined) return 'none';
    if (input.loadedSkus.includes(input.sku)) return 'found';
    if (input.isPending) return 'searching';
    if (!input.hasNextPage) return 'notFound';
    if (input.nextPageFailed || input.pagesLoaded >= DEEP_LINK_MAX_PAGES) return 'beyondLoaded';
    return 'searching';
}
